/**
 * parkFurniture.ts — What a green is made of, and where each piece of it goes.
 *
 * The greens were the one open surface in the town with nothing on them, which
 * from this camera read as a plain block of colour rather than as a place. A
 * street can't be furnished — `AGENTS.md` is emphatic that nothing solid may
 * stand on or beside a footway, which is why the roads get paint instead (see
 * `markings.ts`) — but a park is exactly where the furniture a town does have
 * belongs.
 *
 * ## Why a green gets a whole kit rather than one prop
 *
 * The first version of this gave each green a single piece and the town three
 * pieces in total. That is the right instinct for a *landmark* and the wrong one
 * for a *park*: one bench on 200 units of mown grass is a patch of grass with a
 * bench on it, which is what it looked like. What makes somewhere read as a park
 * from overhead is the layout — a path round the inside of it, something in the
 * middle, seats on the path, planting in the corners — so that is what a green
 * gets, in proportion to how much room it has.
 *
 * The fountain stays rare: it is the one piece that stands in the middle and the
 * only one worth walking to look at, so the biggest green in the town gets it
 * and the others get a bench and a bed apiece.
 *
 * Where each piece goes is a judgement call, so it lives here rather than in the
 * renderer, in the same shape as `yards.ts` and `markings.ts`: pure geometry
 * over the level, tested here, with `render/world.ts` only building what it's
 * handed.
 *
 * **None of it is solid**, like the planting and the boundary hedge. The
 * validator proved the walk over the level's own rects; a renderer-scattered
 * obstacle is one it never saw, and the last thing a green should be able to do
 * is pinch the route to the grass the dog has to reach.
 */
import { type Rect } from './level';
import type { Spot } from './grass';
import { hash2 } from '../utils/math';

/** The pieces a green can carry. */
export type FurnitureKind = 'fountain' | 'bench' | 'kiosk' | 'postbox' | 'bed';

/** A piece of furniture, at a point on the ground plane. */
export interface FurniturePiece {
  kind: FurnitureKind;
  x: number;
  y: number;
}

export interface FurnishSpec {
  /** Every green on the level, in and out of bounds. */
  parks: readonly Rect[];
  /** The playable rectangle; greens outside it are left bare. */
  bounds: Rect;
  /** Points nothing may crowd — the bins and the doorstep. */
  keepOut: readonly Spot[];
  /** How far a piece must stay from one of those points. */
  clearance: number;
  /** The smallest green worth furnishing, on its shorter side. */
  minPark: number;
  /** A green needs at least this much room before it gets the fountain. */
  fountainPark: number;
  /** How far in from the kerb the path runs — and so where an edge piece stands. */
  inset: number;
  /** How wide that path is. */
  pathWidth: number;
  /** A green wider than this gets two benches rather than one. */
  twoBenchPark: number;
}

/**
 * Choose the furniture for a level's greens.
 *
 * Four rules:
 *
 * 1. **Only greens the player walks.** The surround ring is drawn by the same
 *    placers as the town but deliberately plainer (see `levelgen/surround.ts`);
 *    a bench out there is detail nobody ever gets near.
 * 2. **The biggest green gets the fountain**, if it has the room. It's the
 *    widest piece and the only one that stands in the middle, so a small green
 *    would be all fountain — and one fountain in a town is what makes it worth
 *    walking to.
 * 3. **Seats and the tall pieces go on the south edge**, on the path. Anything
 *    tall is drawn about 0.63 × its height *north* on screen (`AGENTS.md` → the
 *    camera tilt), so a piece on the south edge covers its own green, where a
 *    piece on the north edge would cover the pavement — and the walker on it.
 * 4. **The north corners get the flower beds**, which are flat enough to sit
 *    under that rule rather than beside it, and which is what stops the far half
 *    of a green being bare mown nothing.
 */
export function planParkFurniture(spec: FurnishSpec): FurniturePiece[] {
  const { parks, bounds, minPark, fountainPark, inset } = spec;

  const usable = parks
    .filter((p) => Math.min(p.w, p.h) >= minPark && insideRect(p, bounds))
    // Biggest first, so the fountain lands on the green with the most room
    // around it and the ordering doesn't depend on how the generator happened
    // to emit its parks.
    .sort((a, b) => b.w * b.h - a.w * a.h);

  const out: FurniturePiece[] = [];
  usable.forEach((park, i) => {
    const keep = (kind: FurnitureKind, spot: Spot | null): void => {
      if (spot && clearOfObjectives(spot, spec)) out.push({ kind, x: spot.x, y: spot.y });
    };

    // The middle. One town, one fountain — and only if the green can hold it
    // with grass showing all round.
    if (i === 0 && Math.min(park.w, park.h) >= fountainPark) {
      keep('fountain', centreOf(park));
    }

    // Seats, on the south run of the path. Two only where the green is wide
    // enough that one in the middle would look lost.
    const seatY = park.y + park.h - inset;
    const seats =
      park.w >= spec.twoBenchPark
        ? [park.x + park.w * 0.32, park.x + park.w * 0.68]
        : [park.x + park.w / 2];
    for (const x of seats) keep('bench', { x, y: seatY });

    // The tall corner piece, in one south corner. Which piece comes from the
    // green's own position, so a level looks the same on every run and two
    // greens side by side don't both get the kiosk.
    keep(cornerKind(park), cornerSpot(park, inset, spec));

    // Planting in the two north corners — the half of a green the seats and the
    // kiosk leave empty. Inside the loop rather than on it: a bed is a patch of
    // earth, and one laid across the gravel reads as a pot standing on a path.
    const bedInset = inset + spec.pathWidth;
    for (const x of [park.x + bedInset, park.x + park.w - bedInset]) {
      keep('bed', { x, y: park.y + bedInset });
    }
  });
  return out;
}

/**
 * The loop path just inside a green's edge, as the rectangles that draw it.
 *
 * A park path does two things at once. It is the strongest single signal that a
 * rectangle of grass is a *park* — a mown lawn with a gravel loop round the
 * inside of it is unmistakable from overhead, where a plain green rectangle
 * could be anything — and it is what the benches and the pillar box are
 * standing on, which is why they take the same `inset`.
 *
 * Emitted as four **non-overlapping** rects rather than a frame: they are drawn
 * as coplanar slabs, and two of those sharing a corner z-fight along the seam.
 * The north and south runs take the full width and the two sides fill in
 * between them.
 *
 * Returns nothing for a green too small to hold a loop with grass on both sides
 * of it, which would be a paved yard rather than a park.
 */
export function parkPath(park: Rect, inset: number, width: number): Rect[] {
  const half = width / 2;
  const x0 = park.x + inset - half;
  const y0 = park.y + inset - half;
  const x1 = park.x + park.w - inset + half;
  const y1 = park.y + park.h - inset + half;
  if (x1 - x0 < width * 3 || y1 - y0 < width * 3) return [];

  return [
    { x: x0, y: y0, w: x1 - x0, h: width },
    { x: x0, y: y1 - width, w: x1 - x0, h: width },
    { x: x0, y: y0 + width, w: width, h: y1 - y0 - width * 2 },
    { x: x1 - width, y: y0 + width, w: width, h: y1 - y0 - width * 2 },
  ];
}

/**
 * Which tall piece a green gets, from its own position — so a level looks the
 * same on every run, and two greens side by side don't both get the kiosk.
 */
function cornerKind(park: Rect): FurnitureKind {
  const kinds: FurnitureKind[] = ['kiosk', 'postbox'];
  return kinds[Math.floor(hash2(park.x, park.y) * kinds.length) % kinds.length];
}

function centreOf(park: Rect): Spot {
  return { x: park.x + park.w / 2, y: park.y + park.h / 2 };
}

/**
 * A corner of the green's south edge, on the path.
 *
 * Both corners are tried before giving up: the objectives move around from
 * level to level, and a green whose south-west corner happens to sit on the
 * doorstep usually has a perfectly good south-east one.
 */
function cornerSpot(park: Rect, inset: number, spec: FurnishSpec): Spot | null {
  const west = hash2(park.y, park.x) < 0.5;
  const xs = west
    ? [park.x + inset, park.x + park.w - inset]
    : [park.x + park.w - inset, park.x + inset];
  const y = park.y + park.h - inset;
  for (const x of xs) {
    if (clearOfObjectives({ x, y }, spec)) return { x, y };
  }
  return null;
}

function clearOfObjectives(spot: Spot, spec: FurnishSpec): boolean {
  return spec.keepOut.every((k) => Math.hypot(spot.x - k.x, spot.y - k.y) >= spec.clearance);
}

function insideRect(inner: Rect, outer: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}
